import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  User, Building2, Camera, Mail, Lock, Bell, Shield,
  Save, ArrowLeft, Calendar, MapPin, CreditCard, Eye, EyeOff,
  Loader2, Copy, CheckCircle2, XCircle, Smartphone
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, restaurantAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ProfileSettingsPage = () => {
  const navigate = useNavigate();
  const { user, getAuthToken, refreshUser, isCompanyAccount, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [hostProfileData, setHostProfileData] = useState(null);
  const [legalEntityData, setLegalEntityData] = useState(null);

  // Profile photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const profilePhotoInputRef = React.useRef(null);

  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, verified: false });
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);

  // Wallet state
  const [walletData, setWalletData] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [refundPreference, setRefundPreference] = useState('wallet');
  const [savingPreference, setSavingPreference] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    profilePhotoUrl: '',
    // Password change
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    // Notification preferences
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false
  });

  const [companyFormData, setCompanyFormData] = useState({
    companyName: '',
    companyEmail: '',
    registrationNumber: '',
    vatNumber: '',
    directorName: '',
    companyStartDate: '',
    registeredAddress: '',
    registeredCity: '',
    registeredPostcode: '',
    logoUrl: ''
  });

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }
    fetchProfile();
    load2FAStatus();
  }, [authLoading, isAuthenticated, navigate]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const data = await userAPI.getProfile(token);
      
      setProfileData(data.profile);
      setHostProfileData(data.hostProfile);
      setLegalEntityData(data.legalEntity);
      
      // Populate form data
      setFormData({
        displayName: data.profile?.displayName || data.user?.name || '',
        email: data.user?.email || '',
        profilePhotoUrl: data.profile?.profilePhotoUrl || data.user?.image || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        emailNotifications: data.profile?.notificationPreferences?.email ?? true,
        pushNotifications: data.profile?.notificationPreferences?.push ?? true,
        smsNotifications: data.profile?.notificationPreferences?.sms ?? false
      });
      
      // Populate company data if applicable
      if (data.legalEntity?.type === 'company') {
        setCompanyFormData({
          companyName: data.legalEntity?.companyName || '',
          companyEmail: data.user?.email || '',
          registrationNumber: data.legalEntity?.registrationNumber || '',
          vatNumber: data.legalEntity?.vatNumber || '',
          directorName: data.legalEntity?.directorName || '',
          companyStartDate: data.legalEntity?.companyStartDate || '',
          registeredAddress: data.legalEntity?.address?.street || '',
          registeredCity: data.legalEntity?.address?.city || '',
          registeredPostcode: data.legalEntity?.address?.postcode || '',
          logoUrl: data.profile?.profilePhotoUrl || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ============ Profile Photo Upload ============
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Please upload a JPG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5 MB', variant: 'destructive' });
      return;
    }

    setUploadingPhoto(true);
    try {
      const token = getAuthToken();
      const result = await userAPI.uploadProfilePhoto(file, token);
      setFormData(prev => ({ ...prev, profilePhotoUrl: result.url }));
      await refreshUser();
      toast({ title: 'Photo updated', description: 'Your profile photo has been updated' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Could not upload photo', variant: 'destructive' });
    } finally {
      setUploadingPhoto(false);
      if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
    }
  };

  // ============ 2FA Handlers ============
  const load2FAStatus = async () => {
    try {
      const token = getAuthToken();
      const status = await userAPI.get2FAStatus(token);
      setTwoFAStatus(status);
    } catch (err) {
      // Not critical — default to disabled
    }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const token = getAuthToken();
      const data = await userAPI.setup2FA(token);
      setTwoFASetupData(data);
      setTwoFACode('');
    } catch (err) {
      toast({ title: 'Setup failed', description: err.response?.data?.detail || 'Could not start 2FA setup', variant: 'destructive' });
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode || twoFACode.length < 6) {
      toast({ title: 'Invalid code', description: 'Please enter a 6-digit code', variant: 'destructive' });
      return;
    }
    setTwoFALoading(true);
    try {
      const token = getAuthToken();
      await userAPI.verify2FA(twoFACode, token);
      setTwoFAStatus({ enabled: true, verified: true });
      setShowBackupCodes(true);
      toast({ title: '2FA Enabled', description: 'Two-factor authentication is now active' });
    } catch (err) {
      toast({ title: 'Verification failed', description: err.response?.data?.detail || 'Invalid code', variant: 'destructive' });
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode) {
      toast({ title: 'Code required', description: 'Enter your authenticator code to disable 2FA', variant: 'destructive' });
      return;
    }
    setTwoFALoading(true);
    try {
      const token = getAuthToken();
      await userAPI.disable2FA(disableCode, token);
      setTwoFAStatus({ enabled: false, verified: false });
      setTwoFASetupData(null);
      setShowDisable2FA(false);
      setDisableCode('');
      toast({ title: '2FA Disabled', description: 'Two-factor authentication has been turned off' });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Could not disable 2FA', variant: 'destructive' });
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      await userAPI.updateProfile({
        displayName: formData.displayName,
        profilePhotoUrl: formData.profilePhotoUrl,
        notificationPreferences: {
          email: formData.emailNotifications,
          push: formData.pushNotifications,
          sms: formData.smsNotifications
        }
      }, token);
      
      await refreshUser();
      
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been saved successfully'
      });
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }
    
    toast({
      title: 'Password Changed',
      description: 'Your password has been updated (demo mode)'
    });
    
    setFormData({ ...formData, currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  const isCompany = user?.accountType === 'company' || legalEntityData?.type === 'company';

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
            </Link>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-[#1F3A68] mb-6">
            {isCompany ? 'Company Settings' : 'Profile Settings'}
          </h1>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 bg-white px-2 sm:px-3 py-1.5 rounded-xl shadow-sm gap-1">
              <TabsTrigger value="profile" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
                {isCompany ? 'Company Profile' : 'Profile'}
              </TabsTrigger>
              <TabsTrigger value="security" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
                Security
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
                Notifications
              </TabsTrigger>
              <TabsTrigger value="wallet" className="data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white">
                Wallet
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {isCompany ? <Building2 size={24} /> : <User size={24} />}
                    {isCompany ? 'Company Information' : 'Personal Information'}
                  </CardTitle>
                  <CardDescription>
                    {isCompany 
                      ? 'Manage your company profile and business details'
                      : 'Manage your personal details and profile photo'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Profile Photo */}
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <img
                        src={formData.profilePhotoUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${formData.displayName}`}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-4 border-[#1F3A68]/20"
                      />
                      <input
                        ref={profilePhotoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleProfilePhotoUpload}
                      />
                      <button
                        onClick={() => profilePhotoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="absolute bottom-0 right-0 w-8 h-8 bg-[#1F3A68] text-white rounded-full flex items-center justify-center hover:bg-[#1F3A68]/80 transition-colors cursor-pointer"
                        title="Change profile photo"
                      >
                        {uploadingPhoto ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                      </button>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {isCompany ? companyFormData.companyName : formData.displayName}
                      </h3>
                      <p className="text-gray-600">{formData.email}</p>
                      {user?.roles?.includes('host') && (
                        <span className="inline-block mt-2 px-2 py-1 bg-[#1F3A68]/10 text-[#1F3A68] text-xs rounded-full">
                          {isCompany ? 'Company Host' : 'Verified Host'}
                        </span>
                      )}
                    </div>
                  </div>

                  {isCompany ? (
                    // Company Profile Fields
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Company Name</Label>
                        <Input
                          value={companyFormData.companyName}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, companyName: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Company Email</Label>
                        <Input
                          value={companyFormData.companyEmail}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div>
                        <Label>Registration Number</Label>
                        <Input
                          value={companyFormData.registrationNumber}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div>
                        <Label>VAT Number</Label>
                        <Input
                          value={companyFormData.vatNumber}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, vatNumber: e.target.value })}
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label>Director / Authorised Person</Label>
                        <Input
                          value={companyFormData.directorName}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, directorName: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Company Start Date</Label>
                        <Input
                          type="date"
                          value={companyFormData.companyStartDate}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Registered Address</Label>
                        <Input
                          value={companyFormData.registeredAddress}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, registeredAddress: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input
                          value={companyFormData.registeredCity}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, registeredCity: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Postcode</Label>
                        <Input
                          value={companyFormData.registeredPostcode}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, registeredPostcode: e.target.value })}
                        />
                      </div>
                    </div>
                  ) : (
                    // Individual Profile Fields
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input
                          value={formData.displayName}
                          onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          value={formData.email}
                          disabled
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                      </div>
                      {legalEntityData?.dateOfBirth && (
                        <div>
                          <Label>Date of Birth</Label>
                          <Input
                            value={legalEntityData.dateOfBirth}
                            disabled
                            className="bg-gray-50"
                          />
                          <p className="text-xs text-gray-500 mt-1">Read only for compliance</p>
                        </div>
                      )}
                      <div>
                        <Label>Profile Photo URL</Label>
                        <Input
                          value={formData.profilePhotoUrl}
                          onChange={(e) => setFormData({ ...formData, profilePhotoUrl: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  )}

                  <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#1F3A68]">
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock size={24} />
                    Security Settings
                  </CardTitle>
                  <CardDescription>
                    Manage your password and account security
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4 max-w-md">
                    <div>
                      <Label>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.currentPassword}
                          onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label>New Password</Label>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div>
                      <Label>Confirm New Password</Label>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <Button 
                      onClick={handlePasswordChange}
                      disabled={!formData.currentPassword || !formData.newPassword}
                      className="bg-[#CB3B3B]"
                    >
                      Change Password
                    </Button>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Shield size={20} />
                      Account Security
                    </h3>
                    <div className="space-y-4">
                      {/* 2FA Status Display */}
                      <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Smartphone size={20} className="text-[#1F3A68]" />
                            <div>
                              <p className="font-medium">Two-Factor Authentication</p>
                              <p className="text-sm text-gray-600">
                                {twoFAStatus.enabled
                                  ? 'Your account is protected with 2FA'
                                  : 'Add an extra layer of security to your account'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {twoFAStatus.enabled ? (
                              <>
                                <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                                  <CheckCircle2 size={16} /> Enabled
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => setShowDisable2FA(!showDisable2FA)}
                                >
                                  Disable
                                </Button>
                              </>
                            ) : (
                              <Button
                                onClick={handleSetup2FA}
                                disabled={twoFALoading}
                                className="bg-[#1F3A68]"
                              >
                                {twoFALoading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                                Enable 2FA
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Disable 2FA form */}
                        {showDisable2FA && twoFAStatus.enabled && (
                          <div className="border-t pt-4 space-y-3">
                            <p className="text-sm text-gray-600">Enter your authenticator code or a backup code to disable 2FA:</p>
                            <div className="flex gap-2">
                              <Input
                                value={disableCode}
                                onChange={(e) => setDisableCode(e.target.value.replace(/\s/g, ''))}
                                placeholder="Enter 6-digit code"
                                maxLength={8}
                                className="max-w-[200px]"
                              />
                              <Button
                                onClick={handleDisable2FA}
                                disabled={twoFALoading || !disableCode}
                                variant="destructive"
                                size="sm"
                              >
                                {twoFALoading ? <Loader2 size={16} className="animate-spin" /> : 'Confirm Disable'}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* 2FA Setup Flow */}
                        {twoFASetupData && !twoFAStatus.enabled && (
                          <div className="border-t pt-4 space-y-4">
                            <div className="space-y-3">
                              <p className="text-sm font-medium">1. Scan this QR code with your authenticator app</p>
                              <p className="text-xs text-gray-500">Use Google Authenticator, Authy, or any TOTP-compatible app</p>
                              <div className="flex justify-center">
                                <img src={twoFASetupData.qrCode} alt="2FA QR Code" className="w-48 h-48" />
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-gray-500 mb-1">Or enter this key manually:</p>
                                <div className="flex items-center justify-center gap-2">
                                  <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono tracking-wider">
                                    {twoFASetupData.secret}
                                  </code>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(twoFASetupData.secret);
                                      toast({ title: 'Copied', description: 'Secret key copied to clipboard' });
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <Copy size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <p className="text-sm font-medium">2. Enter the 6-digit code from your app</p>
                              <div className="flex gap-2">
                                <Input
                                  value={twoFACode}
                                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  placeholder="000000"
                                  maxLength={6}
                                  className="max-w-[200px] text-center text-lg tracking-[0.5em] font-mono"
                                />
                                <Button
                                  onClick={handleVerify2FA}
                                  disabled={twoFALoading || twoFACode.length < 6}
                                  className="bg-[#1F3A68]"
                                >
                                  {twoFALoading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                                  Verify & Enable
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Backup Codes Display */}
                        {showBackupCodes && twoFASetupData?.backupCodes && (
                          <div className="border-t pt-4 space-y-3">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Save your backup codes</p>
                              <p className="text-xs text-amber-700 mb-3">
                                Store these codes in a safe place. Each code can only be used once.
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {twoFASetupData.backupCodes.map((code, i) => (
                                  <code key={i} className="bg-white px-3 py-1.5 rounded text-sm font-mono text-center border">
                                    {code}
                                  </code>
                                ))}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-3 w-full"
                                onClick={() => {
                                  navigator.clipboard.writeText(twoFASetupData.backupCodes.join('\n'));
                                  toast({ title: 'Copied', description: 'Backup codes copied to clipboard' });
                                }}
                              >
                                <Copy size={14} className="mr-2" /> Copy All Codes
                              </Button>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setShowBackupCodes(false); setTwoFASetupData(null); }}
                              className="w-full"
                            >
                              Done — I've saved my codes
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell size={24} />
                    Notification Preferences
                  </CardTitle>
                  <CardDescription>
                    Choose how you want to be notified
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-sm text-gray-600">Receive booking updates and messages via email</p>
                      </div>
                      <Switch
                        checked={formData.emailNotifications}
                        onCheckedChange={async (checked) => {
                          setFormData(prev => ({ ...prev, emailNotifications: checked }));
                          try {
                            const token = getAuthToken();
                            await userAPI.updateProfile({ notificationPreferences: { email: checked, push: formData.pushNotifications, sms: formData.smsNotifications } }, token);
                          } catch {
                            setFormData(prev => ({ ...prev, emailNotifications: !checked }));
                            toast({ title: 'Error', description: 'Failed to save preference', variant: 'destructive' });
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Push Notifications</p>
                        <p className="text-sm text-gray-600">Receive instant notifications in browser</p>
                      </div>
                      <Switch
                        checked={formData.pushNotifications}
                        onCheckedChange={async (checked) => {
                          setFormData(prev => ({ ...prev, pushNotifications: checked }));
                          try {
                            const token = getAuthToken();
                            await userAPI.updateProfile({ notificationPreferences: { email: formData.emailNotifications, push: checked, sms: formData.smsNotifications } }, token);
                          } catch {
                            setFormData(prev => ({ ...prev, pushNotifications: !checked }));
                            toast({ title: 'Error', description: 'Failed to save preference', variant: 'destructive' });
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">SMS Notifications</p>
                        <p className="text-sm text-gray-600">Receive important alerts via text message</p>
                      </div>
                      <Switch
                        checked={formData.smsNotifications}
                        onCheckedChange={async (checked) => {
                          setFormData(prev => ({ ...prev, smsNotifications: checked }));
                          try {
                            const token = getAuthToken();
                            await userAPI.updateProfile({ notificationPreferences: { email: formData.emailNotifications, push: formData.pushNotifications, sms: checked } }, token);
                          } catch {
                            setFormData(prev => ({ ...prev, smsNotifications: !checked }));
                            toast({ title: 'Error', description: 'Failed to save preference', variant: 'destructive' });
                          }
                        }}
                      />
                    </div>
                  </div>

                  <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#1F3A68]">
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Wallet Tab */}
            <TabsContent value="wallet">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard size={24} />
                    Wallet
                  </CardTitle>
                  <CardDescription>
                    View your balance, transaction history, and refund preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <WalletTabContent
                    token={getAuthToken()}
                    walletData={walletData}
                    setWalletData={setWalletData}
                    walletLoading={walletLoading}
                    setWalletLoading={setWalletLoading}
                    refundPreference={refundPreference}
                    setRefundPreference={setRefundPreference}
                    savingPreference={savingPreference}
                    setSavingPreference={setSavingPreference}
                    toast={toast}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// ── Wallet Tab Content (extracted for clarity) ──
function WalletTabContent({
  token, walletData, setWalletData, walletLoading, setWalletLoading,
  refundPreference, setRefundPreference, savingPreference, setSavingPreference, toast
}) {
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setWalletLoading(true);
      try {
        const [wallet, pref] = await Promise.all([
          restaurantAPI.getWalletBalance(token),
          restaurantAPI.getRefundPreference(token),
        ]);
        setWalletData(wallet);
        setRefundPreference(pref.refundMethod || 'wallet');
      } catch (e) {
        console.error('Failed to load wallet:', e);
      } finally {
        setWalletLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSavePreference = async (method) => {
    setSavingPreference(true);
    try {
      await restaurantAPI.setRefundPreference(method, token);
      setRefundPreference(method);
      toast({ title: 'Saved', description: 'Refund preference updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save preference', variant: 'destructive' });
    } finally {
      setSavingPreference(false);
    }
  };

  if (walletLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68]" />
      </div>
    );
  }

  return (
    <>
      {/* Balance */}
      <div className="bg-gradient-to-r from-[#1F3A68] to-[#2d5299] rounded-xl p-6 text-white">
        <p className="text-sm text-white/70 mb-1">Wallet Balance</p>
        <p className="text-4xl font-bold">{'\u00A3'}{(walletData?.balance || 0).toFixed(2)}</p>
        <p className="text-xs text-white/50 mt-2">Credits from refunds and promotions</p>
      </div>

      {/* Refund Preference */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Refund Preference</h3>
        <p className="text-sm text-gray-500 mb-3">Choose how you'd like to receive refunds when a problem is reported.</p>
        <div className="space-y-3">
          <label
            className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
              refundPreference === 'wallet' ? 'border-[#1F3A68] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleSavePreference('wallet')}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              refundPreference === 'wallet' ? 'border-[#1F3A68]' : 'border-gray-300'
            }`}>
              {refundPreference === 'wallet' && <div className="w-3 h-3 rounded-full bg-[#1F3A68]" />}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Credit to Wallet</p>
              <p className="text-xs text-gray-500">Instant credit — use for future orders</p>
            </div>
            {savingPreference && refundPreference === 'wallet' && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1F3A68]" />
            )}
          </label>

          <label
            className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
              refundPreference === 'original' ? 'border-[#1F3A68] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleSavePreference('original')}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              refundPreference === 'original' ? 'border-[#1F3A68]' : 'border-gray-300'
            }`}>
              {refundPreference === 'original' && <div className="w-3 h-3 rounded-full bg-[#1F3A68]" />}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Refund to Original Payment</p>
              <p className="text-xs text-gray-500">Refund back to your card — may take 5-10 business days</p>
            </div>
            {savingPreference && refundPreference === 'original' && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1F3A68]" />
            )}
          </label>
        </div>
      </div>

      {/* Transaction History */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Transaction History</h3>
        {(!walletData?.transactions || walletData.transactions.length === 0) ? (
          <div className="text-center py-8 text-gray-400">
            <CreditCard size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {walletData.transactions.map(txn => (
              <div key={txn.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    txn.type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                  }`}>
                    {txn.type === 'credit' ? '+' : '-'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{txn.description}</p>
                    <p className="text-xs text-gray-400">
                      {txn.createdAt ? new Date(txn.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      }) : ''}
                    </p>
                  </div>
                </div>
                <span className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {txn.type === 'credit' ? '+' : '-'}{'\u00A3'}{Math.abs(txn.amount).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default ProfileSettingsPage;
